import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subscription, fromEvent } from 'rxjs';
import { auditTime } from 'rxjs/operators';

@Component({
  selector: 'app-input-text',
  templateUrl: './input-text.component.html',
  styleUrls: ['./input-text.component.scss'],
})
export class InputTextComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  @ViewChild('bareInput') bareInput?: ElementRef<HTMLInputElement>;
  @ViewChild('bareTextarea') bareTextarea?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('floatingTextarea')
  floatingTextarea?: ElementRef<HTMLTextAreaElement>;
  private multilineFitSub?: Subscription;
  private multilineResizeSub?: Subscription;
  @Input() inputId: string;
  @Input() label: string;
  @Input() isInvalid = false;
  @Input() isDisabled = false;
  @Input() required = false;
  @Input() optionalTag = true;
  @Input() autocomplete = 'off';
  @Input() control: any;
  @Input() maxLength: number = 524288; // HTML5 max length
  @Input() trim = 'blur';
  @Input() errorText: string;
  @Input() inputType: string = 'text';
  @Input() placeholder: string = '';
  /** Optional inputmode attribute (e.g. 'numeric' for digit-only fields). */
  @Input() inputMode: string | null = null;
  /** Native min/max attributes (number spinners); also fed to range messages. */
  @Input() min: number | string | null = null;
  @Input() max: number | string | null = null;
  /**
   * Standalone (no-FormControl) bare usage: drive the value from the parent
   * (`[value]` + inputEvent/keyupEvent), mirroring the checkbox/toggle
   * standalone convention. Ignored when [control] is set.
   */
  @Input() value: string | number | null = null;
  /** Focus the input after view init (dialogs' cdkFocusInitial/autofocus). */
  @Input() autoFocus = false;
  /** Chrome-less variant: drop the .form-control box (in-widget inputs such
   *  as the tags-input chip field own their own container styling). */
  @Input() plain = false;
  /**
   * Admin mode: render just the `.form-control` (standard admin styling, no
   * floating label) so it can sit inside an <app-form-row> that supplies the
   * static label + grid. Default stays floating for the customer UI.
   */
  @Input() bare = false;
  @Input() readonly = false;
  /**
   * Render a <textarea> instead of <input>. Both floating (customer) and bare
   * (admin) grow via fitMultilineTextarea — one implementation. Do not use
   * cdkTextareaAutosize: its ngDoCheck freezes #cart under checkout's Invalid
   * rules CD storm, and on admin it scrolls one line early (#11824).
   */
  @Input() multiline = false;
  /** Minimum visible lines of a multiline field; 3 in bare mode, 2 floating. */
  @Input() rows?: number;
  /** Lines a multiline field grows to before it starts to scroll. */
  @Input() maxRows = 6;
  /** Optional autocorrect / spellcheck hints for the textarea. */
  @Input() autocorrect: string | null = null;
  @Input() spellcheck: boolean | null = null;
  /** Extra class(es) on the control, e.g. 'percentage-field' or 'read-only'. */
  @Input() cssClass = '';
  /**
   * Optional per-error message overrides for the auto-rendered validation
   * (bare mode). Keys: required | minlength | maxlength | email | pattern | …
   * When omitted, sensible admin defaults (admin.global.field.*) are used.
   * An explicit `errorText` still wins over this.
   */
  @Input() customValidationMessages: { [key: string]: string } = {};
  /** Optional data-testid applied to the input/textarea itself (bare mode). */
  @Input() testId: string | null = null;
  /**
   * Hint rendered under the control as the app's `.explanation` (bare mode).
   * Pass a translate key; for parameterized hints pre-translate at the call
   * site (`[helpText]="'key' | translate: {...}"` — a non-key string passes
   * through the pipe unchanged).
   */
  @Input() helpText = '';
  // Optional data-testid applied to the validation-error container so automation
  // can target a specific field's error without relying on generic structural
  // classes. Defaults to null -> attribute is not rendered (no behavior change).
  @Input() errorTestId: string | null = null;
  @Output() keyupEvent = new EventEmitter<KeyboardEvent>();
  /** Raw focus event (call sites validating/selecting on focus). */
  @Output() focusEvent = new EventEmitter<FocusEvent>();
  /** Raw keydown (e.g. to stopPropagation inside a mat-select panel). */
  @Output() keydownEvent = new EventEmitter<KeyboardEvent>();
  @Output() changeEvent = new EventEmitter<Event>();
  @Output() blurEvent = new EventEmitter<Event>();
  @Output() focusoutEvent = new EventEmitter<Event>();
  @Output() inputEvent = new EventEmitter<Event>();

  constructor(private ngZone: NgZone) {}

  ngOnInit() {
    if (!this.inputId) {
      this.inputId = this.generateRandomId();
    }
  }

  ngAfterViewInit(): void {
    if (this.autoFocus) {
      this.bareInput?.nativeElement.focus();
    }
    this.fitMultilineTextarea(this.multilineTextareaElement);
    this.bindMultilineFitSubscription();
    this.bindMultilineResizeSubscription();
  }

  /**
   * Item-details rebuilds `specialNoteFg` on catalog emissions, so `[control]`
   * can become a new FormControl after init. Re-bind valueChanges or the
   * multiline field keeps listening to a control nobody uses.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['control'] || changes['control'].firstChange) {
      return;
    }
    this.bindMultilineFitSubscription();
  }

  ngOnDestroy(): void {
    this.multilineFitSub?.unsubscribe();
    this.multilineResizeSub?.unsubscribe();
  }

  onMultilineInput(event: Event) {
    this.onInput(event);
    this.fitMultilineTextarea(event.target as HTMLTextAreaElement);
  }

  /** Active multiline textarea: floating (customer) or bare (admin). */
  private get multilineTextareaElement(): HTMLTextAreaElement | undefined {
    return (
      this.floatingTextarea?.nativeElement ?? this.bareTextarea?.nativeElement
    );
  }

  /**
   * Programmatic patchValue / store echo — listen to valueChanges instead of
   * CDK ngDoCheck (no per-CD measure). Used for floating and bare multiline.
   */
  private bindMultilineFitSubscription(): void {
    this.multilineFitSub?.unsubscribe();
    this.multilineFitSub = undefined;
    if (!this.control || !this.multiline) {
      return;
    }
    this.multilineFitSub = this.control.valueChanges.subscribe(() => {
      this.fitMultilineTextarea(this.multilineTextareaElement);
    });
    // FormControlDirective writes the new control's value in this CD pass;
    // refit once after it lands in the textarea.
    Promise.resolve().then(() =>
      this.fitMultilineTextarea(this.multilineTextareaElement),
    );
  }

  /** One window resize listener for the life of the multiline field. */
  private bindMultilineResizeSubscription(): void {
    if (this.multilineResizeSub || !this.multiline) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.multilineResizeSub = fromEvent(window, 'resize')
        .pipe(auditTime(16))
        .subscribe(() =>
          this.fitMultilineTextarea(this.multilineTextareaElement),
        );
    });
  }

  /**
   * Grow a multiline textarea without CDK (floating + bare). Never call from
   * ngDoCheck. Line height from computed style (not a hard-coded 21px).
   *
   * Textarea is content-box (`autosize-textarea` mixin), so `style.height` is
   * content only. `scrollHeight` includes padding — subtract padY or bare
   * Bootstrap `.form-control` padding (~12px) is counted twice (#11824 R6).
   */
  private fitMultilineTextarea(ta: HTMLTextAreaElement | undefined) {
    if (!ta) {
      return;
    }
    const computed = getComputedStyle(ta);
    const parsed = parseFloat(computed.lineHeight);
    const line =
      Number.isFinite(parsed) && parsed > 0
        ? parsed
        : parseFloat(computed.fontSize) * 1.2 || 21;
    const padY =
      (parseFloat(computed.paddingTop) || 0) +
      (parseFloat(computed.paddingBottom) || 0);
    const max = this.maxRows * line;
    const minContent = this.minRows * line;
    ta.style.overflowY = 'hidden';
    ta.style.height = 'auto';
    const contentHeight = Math.max(ta.scrollHeight - padY, 0);
    const next = Math.min(Math.max(contentHeight, minContent), max);
    ta.style.height = `${next}px`;
    if (ta.scrollHeight - padY > max) {
      ta.style.overflowY = 'auto';
    }
  }

  generateRandomId(): string {
    return 'id-' + Math.random().toString(36).substring(2, 11);
  }

  /** Bare multiline: the host is the flex column the autosize box stretches in. */
  @HostBinding('class.is-bare-multiline') get isBareMultiline(): boolean {
    return this.bare && this.multiline;
  }

  get minRows(): number {
    return this.rows ?? (this.bare ? 3 : 2);
  }

  get hasValue(): boolean {
    const value = this.control?.value;
    return value != null && String(value).trim().length > 0;
  }

  /**
   * Selects the static `type="number"` input in bare mode. See the template:
   * a bound `[type]` cannot satisfy Angular's `input[type=number]` directive
   * selectors, so NumberValueAccessor and Min/MaxValidator never attach to it.
   */
  get isNumeric(): boolean {
    return this.inputType === 'number';
  }

  /** True when the field should show an error (explicit isInvalid or a touched invalid control). */
  get showError(): boolean {
    return (
      this.isInvalid ||
      !!(
        this.control?.invalid &&
        (this.control?.dirty || this.control?.touched)
      )
    );
  }

  /** Translation key for the current validation error (bare mode auto-render). */
  get errorMessageKey(): string {
    const errors = this.control?.errors;
    if (!errors) {
      return '';
    }
    const custom = this.customValidationMessages || {};
    if (errors['required'] || errors['whitespace']) {
      return custom['required'] || 'admin.global.field.required';
    }
    if (errors['minlength']) {
      return custom['minlength'] || 'admin.global.field.minLength';
    }
    if (errors['maxlength']) {
      return custom['maxlength'] || 'admin.global.field.maxLength';
    }
    // Numeric range. errorMessageParams already built the interpolation for
    // these, but there was no key to interpolate into, so both fell through to
    // the fallback below and rendered raw (#11331).
    if (errors['min'] || errors['max']) {
      const errorKey = errors['min'] ? 'min' : 'max';
      if (custom[errorKey]) {
        return custom[errorKey];
      }
      // rangeValue and maxNumber are already translated into all 13 languages,
      // so they are preferred over adding new keys. minNumber is new and is
      // English-only until a translation pass — see the PR.
      if (this.min != null && this.max != null) {
        return 'admin.global.field.rangeValue';
      }
      return errors['min']
        ? 'admin.global.field.minNumber'
        : 'admin.global.field.maxNumber';
    }
    const firstKey = Object.keys(errors)[0];
    // NOT `admin.global.field.validation`: that is not a row in the catalogue,
    // it exists only as a namespace because `…validation.invalidDate` is one,
    // so `| translate` resolves it to an object and renders nothing useful.
    // Adding it as a leaf would collide with its own children and take the
    // whole admin language file down with it.
    return custom[firstKey] || 'admin.global.field.invalid';
  }

  /** Interpolation params for the current validation error (e.g. min/max length). */
  get errorMessageParams(): { [key: string]: any } {
    const errors = this.control?.errors;
    if (!errors) {
      return {};
    }
    if (errors['minlength']) {
      return { minLength: errors['minlength'].requiredLength };
    }
    if (errors['maxlength']) {
      return { maxLength: errors['maxlength'].requiredLength };
    }
    if (errors['min'] || errors['max']) {
      return this.rangeParams(errors);
    }
    return {};
  }

  /**
   * `{min,max}` for the range messages. Prefers the component inputs, so the
   * message quotes the bound the field advertises, and falls back to the
   * validator's own payload for controls whose bounds come from the FormGroup
   * rather than from `[min]`/`[max]`.
   */
  private rangeParams(errors: { [key: string]: any }): { [key: string]: any } {
    const min = this.min ?? errors['min']?.min;
    const max = this.max ?? errors['max']?.max;
    return {
      // `min`/`max` for rangeValue; `minNumber`/`maxNumber` for the
      // single-bound keys, which already use those placeholder names.
      ...(min != null && { min, minNumber: min }),
      ...(max != null && { max, maxNumber: max }),
    };
  }

  onKeyup(event: KeyboardEvent) {
    this.keyupEvent.emit(event);
  }

  onInput(event: Event) {
    this.inputEvent.emit(event);
  }

  onChange(event: Event) {
    this.changeEvent.emit(event);
  }

  onBlur(event: Event) {
    this.blurEvent.emit(event);
  }

  onFocusout(event: Event) {
    this.focusoutEvent.emit(event);
  }
}
